import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import MaterialIcon from './MaterialIcon';

// Sits in the portal topbar for all roles.
// Sprint 4: uses Supabase Realtime instead of 30-second polling.

export const NotificationBell = () => {
    const { user, impersonating } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const fetchInFlightRef = useRef(false);

    const fetchNotifications = useCallback(async () => {
        if (!user || fetchInFlightRef.current) return;
        fetchInFlightRef.current = true;
        try {
            const [notes, count] = await Promise.all([
                notificationService.getForUser(user.id),
                notificationService.getUnreadCount(user.id),
            ]);
            setNotifications(notes);
            setUnreadCount(count);
        } catch (error) {
            console.warn('Notification refresh failed:', error?.message || error);
        } finally {
            fetchInFlightRef.current = false;
        }
    }, [user]);

    // Initial load + Realtime subscription
    useEffect(() => {
        fetchNotifications();

        if (!user) return;
        const channel = notificationService.subscribeForUser(user.id, () => {
            fetchNotifications();
        });

        return () => notificationService.unsubscribe(channel);
    }, [user, fetchNotifications]);

    useEffect(() => {
        const handleNotificationRead = () => fetchNotifications();
        window.addEventListener('notifications_read', handleNotificationRead);
        return () => window.removeEventListener('notifications_read', handleNotificationRead);
    }, [fetchNotifications]);

    // Handle click outside to close dropdown/panel
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };

        if (isOpen) {
            document.addEventListener('pointerdown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
            return () => {
                document.removeEventListener('pointerdown', handleClickOutside);
                document.removeEventListener('keydown', handleEscape);
            };
        }

        return undefined;
    }, [isOpen]);

    const handleMarkAllRead = async () => {
        if (!user || impersonating) return;
        await notificationService.markAllRead(user.id);
        fetchNotifications();
    };

    const resolveActionUrl = (notification) => {
        const actionUrl = notification.action_url;
        if (actionUrl) {
            const accMatch = actionUrl.match(/^\/acc\/applications\/([^/?#]+)/);
            if (accMatch) {
                return `/portal?tab=accReview&acc=${encodeURIComponent(accMatch[1])}`;
            }
            return actionUrl;
        }

        if (notification.related_entity_type === 'acc_application' && notification.related_entity_id) {
            return `/portal?tab=accReview&acc=${encodeURIComponent(notification.related_entity_id)}`;
        }
        if (notification.related_entity_type === 'message_thread' && notification.related_entity_id) {
            return `/portal?tab=messages&thread=${encodeURIComponent(notification.related_entity_id)}`;
        }
        if (notification.related_entity_type === 'document' && notification.related_entity_id) {
            return `/portal?tab=documents&document=${encodeURIComponent(notification.related_entity_id)}`;
        }
        if (notification.related_entity_type === 'board_meeting' && notification.related_entity_id) {
            return `/portal?tab=events&event=${encodeURIComponent(notification.related_entity_id)}`;
        }
        return null;
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.read && !impersonating) {
            await notificationService.markEntityNotificationsRead({
                userId: user.id,
                entityType: notification.related_entity_type,
                entityId: notification.related_entity_id,
                notificationId: notification.id,
            });
            await fetchNotifications();
        }
        setIsOpen(false);

        const nextUrl = resolveActionUrl(notification);
        if (!nextUrl) return;
        if (/^https?:\/\//i.test(nextUrl)) {
            window.location.assign(nextUrl);
            return;
        }
        navigate(nextUrl);
    };

    const getNotificationIcon = (eventType = '') => {
        const t = eventType.toUpperCase();
        if (t.includes('DOCUMENT')) return <MaterialIcon name="description" size={20} className="text-secondary" />;
        if (t.includes('USER_CREATED') || t.includes('PROFILE')) return <MaterialIcon name="person_add" size={20} className="text-secondary" />;
        if (t.includes('ACC')) return <MaterialIcon name="shield_check" size={20} className="text-secondary" />;
        if (t.includes('MESSAGE') || t.includes('INQUIRY')) return <MaterialIcon name="chat_bubble" size={20} className="text-secondary" />;
        if (t.includes('SETTING')) return <MaterialIcon name="settings" size={20} className="text-slate-500" />;
        if (t.includes('EVENT')) return <MaterialIcon name="calendar_today" size={20} className="text-secondary" />;
        return <MaterialIcon name="info" size={20} className="text-secondary" />;
    };

    const formatTimeAgo = (dateString) => {
        const diffInSeconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 172800) return 'Yesterday';
        return `${Math.floor(diffInSeconds / 86400)}d ago`;
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative touch-target rounded-full hover:bg-section-bg transition-colors flex items-center justify-center"
                aria-label="Notifications"
                aria-expanded={isOpen}
            >
                <MaterialIcon name="notifications" size={24} className={unreadCount > 0 ? "text-red-700" : "text-primary-dark"} />

                {unreadCount > 0 && (
                    <span className="absolute top-2 right-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-700 px-1 text-[10px] font-bold text-white ring-2 ring-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="max-md:fixed max-md:left-3 max-md:right-3 max-md:top-[max(4.75rem,env(safe-area-inset-top))] max-md:bottom-[max(1rem,env(safe-area-inset-bottom))] md:absolute md:right-0 md:top-full md:mt-2 md:w-96 bg-white rounded-2xl warm-tactile-shadow border border-amber-100/40 overflow-hidden z-[120] origin-top-right flex flex-col"
                    >
                        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-amber-100/40 bg-section-bg/70 flex-shrink-0">
                            <div>
                                <h3 className="font-bold text-primary-dark">Notifications</h3>
                                <p className="text-xs font-medium text-slate-500 md:hidden">{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}</p>
                            </div>
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllRead}
                                    className="min-h-[48px] inline-flex items-center gap-1.5 px-3 rounded-full text-xs font-bold text-secondary hover:bg-white transition-colors"
                                >
                                    <MaterialIcon name="done_all" size={17} />
                                    Mark all read
                                </button>
                            )}
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto">
                            {notifications.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                                    <div className="w-16 h-16 bg-section-bg rounded-2xl flex items-center justify-center mb-4 warm-tactile-shadow">
                                        <MaterialIcon name="notifications" size={32} className="text-secondary/60" />
                                    </div>
                                    <p className="text-sm font-bold text-primary-dark">No notifications</p>
                                    <p className="text-xs font-medium text-slate-500 mt-1">You're all caught up.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-amber-100/40">
                                    {notifications.map((notification) => (
                                        <button
                                            key={notification.id}
                                            onClick={() => handleNotificationClick(notification)}
                                            className={`w-full min-h-[72px] text-left p-4 hover:bg-section-bg/60 transition-colors flex gap-3 ${
                                                !notification.read
                                                    ? 'bg-secondary/10 border-l-4 border-l-secondary'
                                                    : 'bg-white border-l-4 border-l-transparent'
                                            }`}
                                        >
                                            <div className={`mt-0.5 w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${
                                                !notification.read ? 'bg-white warm-tactile-shadow' : 'bg-section-bg'
                                            }`}>
                                                {getNotificationIcon(notification.event_type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm leading-relaxed ${!notification.read ? 'text-primary-dark font-bold' : 'text-slate-600 font-medium'}`}>
                                                    {notification.message}
                                                </p>
                                                <p className="text-xs font-bold text-slate-400 mt-1">
                                                    {formatTimeAgo(notification.created_at)}
                                                </p>
                                            </div>
                                            {!notification.read && (
                                                <div className="w-2 h-2 rounded-full bg-secondary mt-1.5 shrink-0" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
